import { apiRequest } from './http-client';
import type {
  Lead,
  LeadWebformAttributes,
  LeadWebformThemeAttributes,
  VobWebformRuntimeConfig,
  VobWebformSubmitPayload,
} from './types';

function omitEmptyTheme(
  theme: LeadWebformThemeAttributes
): LeadWebformThemeAttributes | undefined {
  const entries = Object.entries(theme).filter(
    ([, value]) => typeof value === 'string' && value.trim().length > 0
  );
  if (entries.length === 0) {
    return undefined;
  }
  return Object.fromEntries(entries) as LeadWebformThemeAttributes;
}

export function mergeLeadEmbedAttributes(
  lead: Lead,
  attributes?: LeadWebformAttributes
): Lead {
  if (!attributes) {
    return lead;
  }

  const merged: Lead = { ...lead };

  if (attributes.instance?.trim()) {
    merged.instance = attributes.instance.trim();
  }
  if (attributes.sendTo?.trim()) {
    merged.sendTo = attributes.sendTo.trim();
  }
  if (attributes.emailTemplateId?.trim()) {
    merged.emailTemplateId = attributes.emailTemplateId.trim();
  }
  if (attributes.redirectOnSuccessUrl?.trim()) {
    merged.redirectOnSuccessUrl = attributes.redirectOnSuccessUrl.trim();
  }
  if (attributes.queue !== undefined) {
    merged.queue = attributes.queue;
  }
  if (attributes.apiToken?.trim()) {
    merged.apiToken = attributes.apiToken.trim();
  }

  const theme = attributes.theme ? omitEmptyTheme(attributes.theme) : undefined;
  if (theme) {
    merged.theme = theme;
  }

  return merged;
}

export function buildLeadFromForm(
  form: VobWebformSubmitPayload,
  runtime: VobWebformRuntimeConfig
): Lead {
  const payorId = Number(form.payor_id);
  const payorPlanId = Number(form.payor_plan_id);

  const lead: Lead = {
    clientId: runtime.clientId,
    facilityId: runtime.facilityId,
    firstName: form.first_name!.trim(),
    lastName: form.last_name!.trim(),
    birthDate: form.date_of_birth!.trim(),
    email: form.email?.trim() || undefined,
    phone: form.phone!.trim(),
    payorId,
    payorName: form.payor_name?.trim() || undefined,
    payorPlanId: payorPlanId > 0 && !Number.isNaN(payorPlanId) ? payorPlanId : undefined,
    memberId: form.member_id!.trim(),
    webformNotes: form.webform_notes?.trim() || undefined,
    webformOrigin: form.webform_origin,
    campaign:
      form.campaign && Object.keys(form.campaign).length > 0
        ? form.campaign
        : undefined,
  };

  return mergeLeadEmbedAttributes(lead, runtime.webformAttributes);
}

export async function createLead(
  apiBase: string,
  authToken: string,
  lead: Lead
): Promise<Lead> {
  const response = await apiRequest<Lead>(apiBase, '/estimation/v2/lead', {
    method: 'POST',
    authToken,
    body: lead,
  });

  if (response.hasErrors || !response.entity) {
    throw new Error(response.errorMessage ?? 'Failed to queue lead');
  }

  return response.entity;
}
